
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Download, ExternalLink, Lock } from "lucide-react";
import { Event } from "@/types/event";
import { useToast } from "@/hooks/use-toast";
import { useSubscription } from "@/hooks/useSubscription";

interface CalendarIntegrationProps {
  event: Event;
}

export const CalendarIntegration = ({ event }: CalendarIntegrationProps) => {
  const { toast } = useToast();
  const { subscription } = useSubscription();
  const [downloading, setDownloading] = useState(false);

  const generateICSFile = () => {
    const dateOptions = event.options.filter(option => option.type === 'date');
    const timeOptions = event.options.filter(option => option.type === 'time');
    const locationOptions = event.options.filter(option => option.type === 'location');

    // Use the most voted date and time, or first available
    const bestDate = dateOptions.sort((a, b) => b.votes - a.votes)[0];
    const bestTime = timeOptions.sort((a, b) => b.votes - a.votes)[0];
    const bestLocation = locationOptions.sort((a, b) => b.votes - a.votes)[0];

    if (!bestDate) {
      toast({
        title: "No date available",
        description: "This event doesn't have any date options to add to calendar.",
        variant: "destructive",
      });
      return;
    }

    // Format date and time
    const eventDate = new Date(bestDate.value);
    if (bestTime) {
      const [hours, minutes] = bestTime.value.split(':');
      eventDate.setHours(parseInt(hours), parseInt(minutes));
    }

    const endDate = new Date(eventDate.getTime() + 2 * 60 * 60 * 1000); // 2 hours duration

    const formatDate = (date: Date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Event Voting App//Event//EN',
      'BEGIN:VEVENT',
      `DTSTART:${formatDate(eventDate)}`,
      `DTEND:${formatDate(endDate)}`,
      `SUMMARY:${event.name}`,
      `DESCRIPTION:${event.description || 'Event created through Event Voting App'}`,
      bestLocation ? `LOCATION:${bestLocation.value}` : '',
      `ORGANIZER:CN=${event.hostName}`,
      `UID:${event.id}@eventvoting.app`,
      'STATUS:CONFIRMED',
      'BEGIN:VALARM',
      'TRIGGER:-PT15M',
      'ACTION:DISPLAY',
      'DESCRIPTION:Event reminder',
      'END:VALARM',
      'END:VEVENT',
      'END:VCALENDAR'
    ].filter(line => line).join('\r\n');

    return icsContent;
  };

  const downloadICS = () => {
    setDownloading(true);
    try {
      const icsContent = generateICS();
      if (!icsContent) return;

      const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${event.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.ics`;
      a.click();
      window.URL.revokeObjectURL(url);

      toast({
        title: "Calendar file downloaded",
        description: "The .ics file has been downloaded. Import it to your calendar app.",
      });
    } catch (error) {
      console.error('Error generating calendar file:', error);
      toast({
        title: "Error",
        description: "Failed to generate calendar file.",
        variant: "destructive",
      });
    } finally {
      setDownloading(false);
    }
  };

  const generateICS = () => {
    return generateICSFile();
  };

  const openGoogleCalendar = () => {
    const dateOptions = event.options.filter(option => option.type === 'date');
    const timeOptions = event.options.filter(option => option.type === 'time');
    const locationOptions = event.options.filter(option => option.type === 'location');

    const bestDate = dateOptions.sort((a, b) => b.votes - a.votes)[0];
    const bestTime = timeOptions.sort((a, b) => b.votes - a.votes)[0];
    const bestLocation = locationOptions.sort((a, b) => b.votes - a.votes)[0];

    if (!bestDate) {
      toast({
        title: "No date available",
        description: "This event doesn't have any date options to add to calendar.",
        variant: "destructive",
      });
      return;
    }

    const eventDate = new Date(bestDate.value);
    if (bestTime) {
      const [hours, minutes] = bestTime.value.split(':');
      eventDate.setHours(parseInt(hours), parseInt(minutes));
    }

    const endDate = new Date(eventDate.getTime() + 2 * 60 * 60 * 1000);

    const formatGoogleDate = (date: Date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: event.name,
      dates: `${formatGoogleDate(eventDate)}/${formatGoogleDate(endDate)}`,
      details: event.description || 'Event created through Event Voting App',
      location: bestLocation?.value || '',
    });

    window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, '_blank');
  };

  if (!subscription.subscribed) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Calendar Integration
            <Badge variant="secondary" className="ml-2">PRO</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Upgrade to Pro to automatically add events to your calendar.
          </p>
          <div className="p-4 bg-muted rounded-lg text-center">
            <p className="text-sm text-muted-foreground mb-3">
              ✓ Google Calendar integration<br />
              ✓ Download .ics files<br />
              ✓ Automatic event reminders
            </p>
            <Button variant="default" size="sm">
              Upgrade to Pro - $2.95/month
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Add to Calendar
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Add this event to your calendar using the most voted options.
        </p>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            onClick={openGoogleCalendar}
            variant="outline"
            className="flex-1"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Google Calendar
          </Button>
          
          <Button
            onClick={downloadICS}
            disabled={downloading}
            variant="outline"
            className="flex-1"
          >
            <Download className="w-4 h-4 mr-2" />
            {downloading ? "Generating..." : "Download .ics"}
          </Button>
        </div>

        <div className="text-xs text-muted-foreground">
          Calendar entries will use the most voted date, time, and location options.
        </div>
      </CardContent>
    </Card>
  );
};
